import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const ANIMATED_EXTENSIONS = /\.(webp|gif|apng)(\?|#|$)/i;

interface AnimatedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Milliseconds between animation replays (0 disables replay refreshes). */
  replayInterval?: number;
}

/**
 * Drop-in `<img>` replacement that periodically restarts animated
 * WebP / GIF / APNG images.  For static formats it renders a plain img.
 *
 * Works by fetching the image once as a Blob, then creating a fresh
 * Object URL on each replay cycle so the browser re-decodes the frames.
 */
export function AnimatedImage({
  src,
  replayInterval = 0,
  ...imgProps
}: AnimatedImageProps) {
  const animated = src ? ANIMATED_EXTENSIONS.test(src) : false;
  const shouldReplay = animated && replayInterval > 0;

  const blobRef = useRef<Blob | null>(null);
  const urlRef = useRef<string | null>(null);
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => {
    if (!blobRef.current) return;
    const prev = urlRef.current;
    const next = URL.createObjectURL(blobRef.current);
    urlRef.current = next;
    setDisplayUrl(next);
    if (prev) URL.revokeObjectURL(prev);
  }, []);

  // Fetch image as blob once
  useEffect(() => {
    if (!shouldReplay || !src) return;
    let cancelled = false;

    fetch(src)
      .then((r) => r.blob())
      .then((blob) => {
        if (cancelled) return;
        blobRef.current = blob;
        refresh();
        setLoaded(true);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      blobRef.current = null;
      setDisplayUrl(null);
      setLoaded(false);
    };
  }, [src, shouldReplay, refresh]);

  // Replay timer — starts after blob is loaded
  useEffect(() => {
    if (!shouldReplay || !loaded) return;
    const timer = setInterval(refresh, replayInterval);
    return () => clearInterval(timer);
  }, [shouldReplay, loaded, replayInterval, refresh]);

  // Lock aspect ratio by default — avatars + workspace logos rendered
  // through AnimatedImage must never squash when their container isn't
  // square. Callers can still override via Tailwind utilities like
  // `object-contain` / `object-fill` (twMerge in cn() resolves the
  // conflict in favor of the explicit caller class).
  const className = cn("object-cover", imgProps.className);

  if (!shouldReplay || !displayUrl) {
    return <img src={src} {...imgProps} className={className} />;
  }

  return <img src={displayUrl} {...imgProps} className={className} />;
}
